import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatMenuTrigger } from "@angular/material/menu";
import { Router } from "@angular/router";
import { ConfirmationDialogComponent } from "@dialogs/confirmation/confirmation.component";
import { CreateDialogComponent } from "@dialogs/create/create.component";
import { DownloadDialogComponent } from "@dialogs/download/download.component";
import { PreviewDialogComponent } from "@dialogs/preview/preview.component";
import {
  faBookmark as faBookmarkOutline,
  faCopy,
  faEllipsisH,
  faEye,
  faInfoCircle,
} from "@fortawesome/pro-light-svg-icons";
import { faBookmark } from "@fortawesome/pro-solid-svg-icons";
import { CommonService } from "@services/common.service";
import { DownloadService } from "@services/download.service";
import { CanvasService } from "@services/canvas.service";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

import { ROUTE_URL } from "../../constants/routes";

@Component({
    selector: "app-canvas-tile",
    templateUrl: "./canvas-tile.component.html",
    styleUrls: ["./canvas-tile.component.scss"],
    standalone: false
})
export class CanvasTileComponent implements OnInit {
  @Input() id: any;
  @Input() favorited: any;
  @Input() editable: any;
  @Input() isSystem: any;
  @Input() type: any;
  @Input() category: any;
  @Input() category_id: any;
  @Input() title: any;
  @Input() description: any;
  @Input() author: any;
  @Input() report_listing: any;
  @Input() updated: any;
  @Input() canvasType: any;

  public ROUTE_URL = ROUTE_URL;

  faBookmark = faBookmark;
  faBookmarkOutline = faBookmarkOutline;
  faEye = faEye;
  faCopy = faCopy;
  faInfoCircle = faInfoCircle;
  faEllipsisH = faEllipsisH;

  hasEdit: boolean = true;
  ellipsisShown: boolean = false;
  messageDetail: string = '';

  constructor(
    public downloadService: DownloadService,
    private canvasService: CanvasService,
    public commonService: CommonService,
    public dialog: MatDialog,
    private router: Router
  ) {}
  @ViewChild(MatMenuTrigger) trigger!: MatMenuTrigger;

  openDownloadDialog(event: any, id: number, title: string): void {
    event.stopPropagation();
    const dialogRef = this.dialog.open(DownloadDialogComponent, {
      width: "350px",
      data: {
        id: id,
        title: title.split(" ").join("_").replace("'", ""),
        allowCSV: false,
        showMessage: false,
        type: this.canvasType,
      },
      panelClass: "canvas-dialog",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result.result) {
        this.downloadService.startExport(
          id,
          this.canvasType,
          result.title,
          result.type,
          null
        );
      }
    });
  }

  openCreateDialog(
    event: any,
    title: string,
    category_id: string,
    canvas_id: string,
    description: string
  ): void {
    event.stopPropagation();

    this.dialog.open(CreateDialogComponent, {
      width: "350px",
      disableClose: true,
      data: {
        title: "DUPLICATED - " + title,
        module_id: category_id,
        canvas_id: canvas_id,
        description: description,
        canvasType: this.canvasType,
      },
      panelClass: "canvas-dialog",
    });
  }

  openDeleteDialog(event: any, id: any): void {
    event.stopPropagation();

    if(this.canvasType == 'report'){
      this.canvasService.getDashboardByReportId(Number(id)).subscribe(
        (success: any) => {
          
          let enterpriseDash = success.enterprise || {};
          let legacyDash = success.legacy || {};

          this.messageDetail = this.commonService.generateDashboardMessage(enterpriseDash, legacyDash);

          this.loadDeleteDialog(id);
        },
  
        (error: any) => {
          if (error.status === 404) this.router.navigate(["/404"]);
          if (error.status === 500) this.router.navigate(["/500"]);
        }
      );
    } else {
      this.loadDeleteDialog(id);
    }

  }

  loadDeleteDialog(id: any): void {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      data: {
        message: "Delete this " + this.canvasType + "?",
        messageDetail: this.messageDetail ?? "",
      },
      panelClass: "canvas-dialog",
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.canvasService.deleteCanvas(id, this.canvasType);
      }
    });
  }

  openPreviewDialog(
    event: any,
    id: number,
    title: string,
    editable: boolean
  ): void {
    event.stopPropagation();
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {};
    dialogConfig.width = "50vw";

    dialogConfig.panelClass = "dialog-with-close";
    dialogConfig.disableClose = false;
    dialogConfig.data = {
      id: id,
      title: title,
      editable: editable,
      showButtons: true,
      canvasType: this.canvasType,
    };
    this.dialog.open(PreviewDialogComponent, dialogConfig);
  }

  closeActionMenu() {
    this.trigger.closeMenu();
  }

  goToReport(editable: boolean, id: number) {
    if (this.canvasType == "report") {
      if (editable) {
        this.router.navigate([ROUTE_URL.editReportAppURL, id]);
      } else {
        this.router.navigate([ROUTE_URL.viewReportAppURL, id]);
      }
    } else {
      if (editable) {
        this.router.navigate([ROUTE_URL.editDashboardAppURL, id]);
      } else {
        this.router.navigate([ROUTE_URL.viewDashboardAppURL, id]);
      }
    }
  }

  returnCanvasUrl(editable: boolean) {
    let route = "";
    if (this.canvasType == "report") {
      if (editable) {
        route = ROUTE_URL.editReportAppURL;
      } else {
        route = ROUTE_URL.viewReportAppURL;
      }
    } else {
      if (editable) {
        route = ROUTE_URL.editDashboardAppURL;
      } else {
        route = ROUTE_URL.viewDashboardAppURL;
      }
    }
    return route;
  }

  getAvatarColor(name: string) {
    //#4c9aff 0%, #64a7ff
    let returnVal: string = "#11AFD1";
    let switchVal = name && name.trim().toLowerCase();
    switch (switchVal) {
      case "quantivate":
        returnVal = "#ff8b00";
        break;
      case "admin":
        returnVal = "#5a4ab4";
        break;
      default:
        returnVal = "#11AFD1";
    }
    return returnVal;
  }

  getInitials(name: string) {
    let returnVal: string = this.commonService.getInitials(name);
    return returnVal;
  }

  formatUpdated(time: string) {
    const displaydTIme = dayjs(time).fromNow();
    return displaydTIme;
  }

  getShortName(name: string) {
    let returnVal: string = "";
    const nameArray = name && name.split(" ");
    if (nameArray && nameArray.length > 1) {
      returnVal =
        nameArray[0] + " " + nameArray[nameArray.length - 1].charAt(0) + ".";
    } else {
      if (nameArray) {
        let str = nameArray[0];
        str = str[0].toUpperCase() + str.slice(1);
        returnVal = str;
      }
    }
    return returnVal;
  }

  checkEdit() {
    if (this.author && this.author.toLowerCase() == "quantivate") {
      this.hasEdit = false;
    } else {
      this.hasEdit = true;
    }
  }

  showDetails(event: any) {
    (
      document.querySelector(
        "[id='" + event.target.id + "'] .image .details"
      ) as HTMLElement
    ).style.opacity = "1";
  }

  hideDetails(event: any) {
    (
      document.querySelector(
        "[id='" + event.target.id + "'] .image .details"
      ) as HTMLElement
    ).style.opacity = "0";
  }

  setClickStyle(event: any) {
    event.stopPropagation();
    let reports = document.querySelectorAll(".tile_list");
    for (let i = 0; i < reports.length; i++) {
      reports[i].classList.remove("clicked");
    }
    event.path[4].classList.add("clicked");
  }

  ngOnInit(): void {
    this.checkEdit();
  }
}
